import { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { useAppNavigate } from "../lib/route-primitives";
import { organizerSupportApi, organizerWeddingsApi } from "../lib/api";
import { Loader2, LifeBuoy, ArrowLeft, Plus, X, Send, Paperclip, RotateCcw, MessageSquare, Headphones, FileImage } from "lucide-react";
import { cn } from "../lib/utils";
import { paths } from "../lib/paths";
import { OrganizerPageHeader } from "../components/organizer-page-header";

const SUBJECT_MIN = 5;
const BODY_MIN = 20;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB — matches server limit
const ALLOWED_MIME_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

const formatTicketNumber = (n: number) => `SUP-${String(n).padStart(4, "0")}`;

const rtfCache = new Map<string, Intl.RelativeTimeFormat>();
function formatRelativeTime(isoString: string, lang: string): string {
  const diff = Math.max(0, Date.now() - new Date(isoString + (isoString.includes("Z") ? "" : "Z")).getTime());
  try {
    let rtf = rtfCache.get(lang);
    if (!rtf) { rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto" }); rtfCache.set(lang, rtf); }
    const secs = Math.floor(diff / 1000);
    if (secs < 60) return rtf.format(-secs, "second");
    const mins = Math.floor(secs / 60);
    if (mins < 60) return rtf.format(-mins, "minute");
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return rtf.format(-hrs, "hour");
    return rtf.format(-Math.floor(hrs / 24), "day");
  } catch { return `${Math.floor(diff / 60000)}m`; }
}
const MAX_ATTACHMENTS = 2;

const STATUS_LABEL_KEYS: Record<string, string> = {
  open: "statusOpen",
  in_review: "statusInReview",
  resolved: "statusResolved",
  closed: "statusClosed",
};

const STATUS_COLORS: Record<string, string> = {
  open: "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400",
  in_review: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400",
  resolved: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400",
  closed: "bg-gray-100 text-gray-500 dark:bg-gray-500/20 dark:text-gray-400",
};

const PRIORITY_COLORS: Record<string, string> = {
  low: "bg-gray-100 text-gray-500",
  normal: "bg-blue-50 text-blue-600",
  high: "bg-orange-100 text-orange-600",
  urgent: "bg-red-100 text-red-600",
};

const PRIORITY_LABEL_KEYS: Record<string, string> = {
  low: "priorityLow",
  normal: "priorityNormal",
  high: "priorityHigh",
  urgent: "priorityUrgent",
};

const CATEGORY_LABEL_KEYS: Record<string, string> = {
  general: "categoryGeneral",
  billing: "categoryBilling",
  technical: "categoryTechnical",
  bug: "categoryBug",
  feature_request: "categoryFeatureRequest",
};

interface TicketRow {
  id: string;
  ticket_number: number;
  subject: string;
  body?: string;
  status: string;
  priority: string;
  category: string;
  organizer_unread: number;
  wedding_slug: string | null;
  created_at: string;
}

interface SupportMessage {
  id: string;
  author_type: "organizer" | "admin";
  content: string;
  attachment_keys: string | null;
  created_at: string;
}

interface WeddingSummary {
  slug: string;
  title: string;
}

function TicketListItem({
  ticket, selected, onClick,
}: {
  ticket: TicketRow; selected: boolean; onClick: () => void;
}) {
  const { t, i18n } = useTranslation();
  const rel = formatRelativeTime(ticket.created_at, i18n.language);
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full text-left px-4 py-3.5 border-b border-primary/5 transition-colors cursor-pointer",
        selected
          ? "bg-primary/10 border-l-2 border-l-primary"
          : "hover:bg-primary/5"
      )}
    >
      {/* Row 1: status badge + time */}
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className={cn(
          "text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-lg",
          STATUS_COLORS[ticket.status] ?? "bg-gray-100 text-gray-500"
        )}>
          {t(`support.${STATUS_LABEL_KEYS[ticket.status] ?? "statusOpen"}`)}
        </span>
        <span className="text-[10px] text-muted-foreground shrink-0">{rel}</span>
      </div>
      {/* Row 2: subject + unread dot */}
      <div className="flex items-center gap-1.5">
        {ticket.organizer_unread > 0 && (
          <span className="h-2 w-2 rounded-full bg-primary animate-pulse shrink-0" />
        )}
        <p className="text-sm font-semibold truncate flex-1">{ticket.subject}</p>
      </div>
      {/* Row 3: body preview */}
      {ticket.body && (
        <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2 leading-relaxed">
          {ticket.body}
        </p>
      )}
      {/* Row 4: ticket number */}
      <p className="mt-1 font-mono text-[10px] text-muted-foreground/60">
        {formatTicketNumber(ticket.ticket_number)}
      </p>
    </button>
  );
}

function TicketListPanel({
  tickets, loading, loadingMore, hasMore, activeTab, selectedId,
  onTabChange, onSelect, onLoadMore, onNew,
}: {
  tickets: TicketRow[]; loading: boolean; loadingMore: boolean; hasMore: boolean;
  activeTab: "active" | "resolved"; selectedId: string | null;
  onTabChange: (tab: "active" | "resolved") => void;
  onSelect: (id: string) => void; onLoadMore: () => void; onNew: () => void;
}) {
  const { t } = useTranslation();
  return (
    <aside className="w-full md:w-72 shrink-0 flex flex-col border-r border-primary/10 overflow-hidden">
      {/* Panel header — title only */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-primary/10 shrink-0">
        <LifeBuoy className="h-4 w-4 text-primary" />
        <span className="text-sm font-semibold">{t("support.title")}</span>
      </div>
      {/* Tabs */}
      <div role="tablist" className="flex border-b border-primary/10 shrink-0">
        {(["active", "resolved"] as const).map((tab) => (
          <button
            key={tab}
            role="tab"
            aria-selected={activeTab === tab}
            onClick={() => onTabChange(tab)}
            className={cn(
              "flex-1 py-3.5 text-xs font-bold uppercase tracking-wider transition-colors cursor-pointer",
              activeTab === tab
                ? "border-b-2 border-primary text-primary"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {tab === "active" ? t("support.tabActive") : t("support.tabResolved")}
          </button>
        ))}
      </div>

      {/* New Ticket button */}
      <div className="px-4 py-3 border-b border-primary/10 shrink-0">
        <button
          onClick={onNew}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5 text-sm font-medium text-primary hover:bg-primary/10 transition-colors cursor-pointer"
        >
          <Plus className="h-4 w-4" />
          {t("support.newTicket")}
        </button>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : tickets.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
            <LifeBuoy className="h-10 w-10 opacity-30" />
            <p className="text-sm">{t("support.noTickets")}</p>
          </div>
        ) : (
          <>
            {tickets.map((tk) => (
              <TicketListItem
                key={tk.id}
                ticket={tk}
                selected={selectedId === tk.id}
                onClick={() => onSelect(tk.id)}
              />
            ))}
            {hasMore && (
              <div className="p-4">
                <button
                  onClick={onLoadMore}
                  disabled={loadingMore}
                  className="w-full rounded-xl border py-2 text-sm font-medium hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  {loadingMore
                    ? <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                    : t("common.loadMore")}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </aside>
  );
}

function EmptyDetailState({ onNew }: { onNew: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 text-muted-foreground p-8">
      <div className="rounded-full bg-primary/10 p-6">
        <MessageSquare className="h-10 w-10 text-primary/50" />
      </div>
      <div className="text-center">
        <p className="text-sm font-medium text-foreground">{t("support.selectTicket")}</p>
        <p className="mt-1 text-xs opacity-60">{t("support.selectTicketHint")}</p>
      </div>
      <button
        onClick={onNew}
        className="flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-medium text-white hover:bg-primary/90 transition-colors cursor-pointer"
      >
        <Plus className="h-4 w-4" />
        {t("support.newTicket")}
      </button>
    </div>
  );
}

function NewTicketModal({
  weddings, onCreated, onClose,
}: {
  weddings: WeddingSummary[]; onCreated: (id: string) => void; onClose: () => void;
}) {
  const { t } = useTranslation();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [weddingSlug, setWeddingSlug] = useState("");
  const [priority, setPriority] = useState("normal");
  const [category, setCategory] = useState("general");
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const previews = useMemo(() => pendingFiles.map((f) => URL.createObjectURL(f)), [pendingFiles]);
  useEffect(() => () => previews.forEach(URL.revokeObjectURL), [previews]);

  const subjectTooShort = subject.trim().length > 0 && subject.trim().length < SUBJECT_MIN;
  const bodyTooShort = body.trim().length > 0 && body.trim().length < BODY_MIN;
  const canSubmit = subject.trim().length >= SUBJECT_MIN && body.trim().length >= BODY_MIN && !submitting;

  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileError("");
    const selected = Array.from(e.target.files ?? []);
    const invalid = selected.find((f) => !ALLOWED_MIME_TYPES.includes(f.type));
    if (invalid) { setFileError(t("support.fileTypeError")); e.target.value = ""; return; }
    const tooBig = selected.find((f) => f.size > MAX_FILE_SIZE);
    if (tooBig) { setFileError(t("support.fileSizeError")); e.target.value = ""; return; }
    const next = [...pendingFiles, ...selected].slice(0, MAX_ATTACHMENTS);
    setPendingFiles(next);
    e.target.value = "";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await organizerSupportApi.create({
        subject: subject.trim(), body: body.trim(),
        weddingSlug: weddingSlug || undefined, priority, category,
      });
      if (res.data?.id) {
        const newId = res.data.id;
        if (pendingFiles.length > 0) {
          try {
            const keys = await organizerSupportApi.uploadAttachments(newId, pendingFiles);
            await organizerSupportApi.sendMessage(newId, "", keys);
          } catch (attachErr) {
            // Ticket was created — navigate but show the attachment error
            setError(attachErr instanceof Error ? attachErr.message : t("support.attachmentError"));
            setSubmitting(false);
            onCreated(newId);
            return;
          }
        }
        onCreated(newId);
      } else {
        setError(t("common.unknownError"));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.unknownError"));
    } finally { setSubmitting(false); }
  };

  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={handleBackdrop}
    >
      <div className="w-full max-w-lg rounded-2xl bg-background border border-primary/10 shadow-2xl flex flex-col max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-primary/10 shrink-0">
          <h2 className="font-serif text-lg font-bold">{t("support.newTicket")}</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            aria-label={t("common.cancel")}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Wedding */}
          <div>
            <label className="mb-1.5 block text-sm font-medium">{t("support.weddingLabel")}</label>
            <select
              value={weddingSlug}
              onChange={(e) => setWeddingSlug(e.target.value)}
              className="w-full rounded-md border border-primary/20 px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 cursor-pointer"
            >
              <option value="">{t("support.generalTicket")}</option>
              {weddings.map((w) => (
                <option key={w.slug} value={w.slug}>{w.title} (/{w.slug})</option>
              ))}
            </select>
          </div>

          {/* Subject */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium">{t("support.subject")}</label>
              <span className={cn("text-xs", subjectTooShort ? "text-destructive" : "text-muted-foreground")}>
                {subject.length}/200
              </span>
            </div>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
              required
              className={cn(
                "w-full rounded-md border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30",
                subjectTooShort ? "border-destructive" : "border-primary/20"
              )}
            />
            {subjectTooShort && <p className="mt-1 text-xs text-destructive">{t("support.minCharsSubject")}</p>}
          </div>

          {/* Body */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium">{t("support.body")}</label>
              <span className={cn("text-xs", bodyTooShort ? "text-destructive" : "text-muted-foreground")}>
                {body.length}/5000
              </span>
            </div>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              maxLength={5000}
              required
              className={cn(
                "w-full resize-none rounded-md border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30",
                bodyTooShort ? "border-destructive" : "border-primary/20"
              )}
            />
            {bodyTooShort && <p className="mt-1 text-xs text-destructive">{t("support.minCharsBody")}</p>}
          </div>

          {/* Priority + Category */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block text-sm font-medium">{t("support.priority")}</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full rounded-md border border-primary/20 px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 cursor-pointer"
              >
                {Object.entries(PRIORITY_LABEL_KEYS).map(([v, k]) => (
                  <option key={v} value={v}>{t(`support.${k}`)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">{t("support.category")}</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full rounded-md border border-primary/20 px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 cursor-pointer"
              >
                {Object.entries(CATEGORY_LABEL_KEYS).map(([v, k]) => (
                  <option key={v} value={v}>{t(`support.${k}`)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Attachments */}
          <div>
            <label className="mb-1.5 block text-sm font-medium">
              {t("support.attachments")}
              <span className="ml-1 text-xs font-normal text-muted-foreground">
                (JPEG/PNG/WebP/GIF · max 5 MB · {MAX_ATTACHMENTS} {t("support.attachments").toLowerCase()})
              </span>
            </label>
            {previews.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2">
                {pendingFiles.map((f, i) => (
                  <div key={i} className="relative group">
                    <img
                      src={previews[i]}
                      alt={f.name}
                      className="h-16 w-16 rounded-md object-cover border border-primary/20"
                    />
                    <button
                      type="button"
                      onClick={() => setPendingFiles(pendingFiles.filter((_, idx) => idx !== i))}
                      className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-white opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    >
                      <X className="h-3 w-3" />
                    </button>
                    <span className="mt-0.5 block truncate text-[10px] text-muted-foreground max-w-[64px]">{f.name}</span>
                  </div>
                ))}
              </div>
            )}
            {pendingFiles.length < MAX_ATTACHMENTS && (
              <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-primary/30 px-3 py-2 text-sm text-muted-foreground hover:bg-muted/50 transition-colors">
                <FileImage className="h-4 w-4 shrink-0" />
                <span>{t("support.attachFiles")}</span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  multiple
                  className="sr-only"
                  onChange={handleFiles}
                />
              </label>
            )}
            {fileError && <p className="mt-1 text-xs text-destructive">{fileError}</p>}
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-primary/20 px-4 py-2 text-sm font-medium hover:bg-muted transition-colors cursor-pointer"
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitting ? t("support.submitting") : t("support.submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ChatDetailPanel({ ticketId, onBack }: { ticketId: string; onBack: () => void }) {
  const { t } = useTranslation();
  const threadRef = useRef<HTMLDivElement>(null);

  const [ticket, setTicket] = useState<{
    id: string; ticket_number: number; subject: string; body: string;
    status: string; priority: string; category: string;
    organizer_unread: number; csat_score: number | null; csat_token: string | null;
    wedding_slug: string | null; created_at: string;
    messages: SupportMessage[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [replyContent, setReplyContent] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  useEffect(() => {
    setLoading(true);
    setTicket(null);
    organizerSupportApi.get(ticketId)
      .then((res) => {
        setTicket(res.data ?? null);
        organizerSupportApi.markRead(ticketId).catch(() => {});
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [ticketId]);

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [ticket?.messages.length]);

  const handleSend = async () => {
    if (!ticket || (!replyContent.trim() && pendingFiles.length === 0) || sending) return;
    setSending(true);
    setSendError("");
    try {
      let uploadedKeys: string[] = [];
      if (pendingFiles.length > 0) {
        uploadedKeys = await organizerSupportApi.uploadAttachments(ticketId, pendingFiles);
      }
      const res = await organizerSupportApi.sendMessage(ticketId, replyContent.trim(), uploadedKeys);
      if (res.data) {
        const newMsg: SupportMessage = {
          id: res.data.id, author_type: "organizer",
          content: replyContent.trim(),
          attachment_keys: uploadedKeys.length ? JSON.stringify(uploadedKeys) : null,
          created_at: res.data.created_at,
        };
        setTicket((prev) => prev ? { ...prev, messages: [...prev.messages, newMsg] } : prev);
        setReplyContent("");
        setPendingFiles([]);
      }
    } catch (err) {
      setSendError(err instanceof Error ? err.message : t("common.unknownError"));
    } finally { setSending(false); }
  };

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!ticket) {
    return <div className="p-6 text-sm text-muted-foreground">{t("common.error")}</div>;
  }

  const isResolved = ticket.status === "resolved";
  const isClosed = ticket.status === "closed";
  const canReply = ticket.status === "open" || ticket.status === "in_review";

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* ── Header ── */}
      <div className="px-6 py-4 border-b border-primary/10 shrink-0">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 mb-3 text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("support.backToList")}
        </button>
        <h2 className="font-serif text-xl font-bold leading-snug">
          <span className="font-mono text-xs font-normal text-muted-foreground mr-2">
            {formatTicketNumber(ticket.ticket_number)}
          </span>
          {ticket.subject}
        </h2>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className={cn("text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-lg", STATUS_COLORS[ticket.status] ?? "bg-gray-100")}>
            {t(`support.${STATUS_LABEL_KEYS[ticket.status] ?? "statusOpen"}`)}
          </span>
          <span className={cn("text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-lg", PRIORITY_COLORS[ticket.priority] ?? "")}>
            {t(`support.${PRIORITY_LABEL_KEYS[ticket.priority] ?? "priorityNormal"}`)}
          </span>
          <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-lg bg-muted text-muted-foreground">
            {t(`support.${CATEGORY_LABEL_KEYS[ticket.category] ?? "categoryGeneral"}`)}
          </span>
          {ticket.wedding_slug && (
            <span className="font-mono text-[10px] px-2 py-0.5 rounded-lg bg-muted text-muted-foreground">
              /{ticket.wedding_slug}
            </span>
          )}
          {isResolved && (
            <button
              onClick={async () => {
                if (!confirm(t("support.reopenConfirm"))) return;
                try {
                  await organizerSupportApi.reopen(ticket.id);
                  setTicket((prev) => prev ? { ...prev, status: "open", csat_score: null, csat_token: null } : prev);
                } catch (err) {
                  alert(err instanceof Error ? err.message : t("common.unknownError"));
                }
              }}
              className="ml-auto flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400 hover:underline transition-colors cursor-pointer"
            >
              <RotateCcw className="h-3 w-3" />
              {t("support.reopen")}
            </button>
          )}
        </div>
      </div>

      {/* ── Thread ── */}
      <div ref={threadRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        <div className="flex justify-center">
          <span className="px-3 py-1 rounded-full bg-primary/10 text-[10px] text-primary/70 font-bold tracking-widest uppercase">
            {new Date(ticket.created_at + (ticket.created_at.includes("Z") ? "" : "Z")).toLocaleDateString()}
          </span>
        </div>

        <MessageBubble
          authorType="organizer"
          content={ticket.body}
          createdAt={ticket.created_at}
          label={t("support.you")}
          attachmentKeys={null}
          ticketId={ticket.id}
        />

        {ticket.messages.map((msg) => (
          <MessageBubble
            key={msg.id}
            authorType={msg.author_type}
            content={msg.content}
            createdAt={msg.created_at}
            label={msg.author_type === "organizer" ? t("support.you") : t("support.admin")}
            attachmentKeys={msg.attachment_keys}
            ticketId={ticket.id}
          />
        ))}
      </div>

      {/* ── CSAT ── */}
      {isResolved && (
        <div className="px-4 pb-2 shrink-0">
          {ticket.csat_score === null ? (
            <CsatRating
              ticketId={ticket.id}
              onRated={(score) => setTicket((prev) => prev ? { ...prev, csat_score: score } : prev)}
            />
          ) : (
            <div className="rounded-xl border border-green-200 dark:border-green-500/30 bg-green-50 dark:bg-green-500/10 px-4 py-3 text-sm text-green-700 dark:text-green-400">
              {t("support.csatThanks")} {"⭐".repeat(ticket.csat_score)}
            </div>
          )}
        </div>
      )}

      {/* ── Closed ── */}
      {isClosed && (
        <div className="px-4 pb-2 shrink-0">
          <p className="rounded-xl border bg-muted px-4 py-3 text-sm text-muted-foreground">
            {t("support.ticketClosed")}
          </p>
        </div>
      )}

      {/* ── Reply ── */}
      {canReply && (
        <ReplyInput
          value={replyContent}
          onChange={setReplyContent}
          onSend={handleSend}
          sending={sending}
          sendError={sendError}
          pendingFiles={pendingFiles}
          onFilesChange={setPendingFiles}
        />
      )}
    </div>
  );
}

function ReplyInput({
  value, onChange, onSend, sending, sendError, pendingFiles, onFilesChange,
}: {
  value: string; onChange: (v: string) => void; onSend: () => void;
  sending: boolean; sendError: string; pendingFiles: File[];
  onFilesChange: (files: File[]) => void;
}) {
  const { t } = useTranslation();
  const [fileError, setFileError] = useState("");

  const previews = useMemo(() => pendingFiles.map((f) => URL.createObjectURL(f)), [pendingFiles]);
  useEffect(() => () => previews.forEach(URL.revokeObjectURL), [previews]);

  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileError("");
    const selected = Array.from(e.target.files ?? []);
    const invalid = selected.find((f) => !ALLOWED_MIME_TYPES.includes(f.type));
    if (invalid) { setFileError(t("support.fileTypeError")); e.target.value = ""; return; }
    const tooBig = selected.find((f) => f.size > MAX_FILE_SIZE);
    if (tooBig) { setFileError(t("support.fileSizeError")); e.target.value = ""; return; }
    const next = [...pendingFiles, ...selected].slice(0, MAX_ATTACHMENTS);
    onFilesChange(next);
    e.target.value = "";
  };

  const canSend = (value.trim().length > 0 || pendingFiles.length > 0) && !sending;

  return (
    <div className="p-4 border-t border-primary/10 shrink-0 bg-background/80 backdrop-blur-sm">
      {previews.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {pendingFiles.map((f, i) => (
            <div key={i} className="relative group">
              <img
                src={previews[i]}
                alt={f.name}
                className="h-14 w-14 rounded-md object-cover border border-primary/20"
              />
              <button
                onClick={() => onFilesChange(pendingFiles.filter((_, idx) => idx !== i))}
                className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-white opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      {(sendError || fileError) && (
        <p className="mb-2 text-xs text-destructive">{sendError || fileError}</p>
      )}
      <div className="flex items-end gap-2 bg-muted/50 rounded-2xl border border-primary/10 p-2 pl-3">
        {pendingFiles.length < MAX_ATTACHMENTS && (
          <label aria-label={t("support.attachments")} className="cursor-pointer text-muted-foreground hover:text-primary transition-colors p-1">
            <Paperclip className="h-4 w-4" />
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              className="sr-only"
              onChange={handleFiles}
            />
          </label>
        )}
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={1}
          placeholder={t("support.replyPlaceholder")}
          disabled={sending}
          className="flex-1 bg-transparent border-none focus:ring-0 text-sm py-1 resize-none placeholder:text-muted-foreground/60 disabled:opacity-50 max-h-32"
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) onSend();
          }}
        />
        <button
          onClick={onSend}
          disabled={!canSend}
          className="bg-primary text-white rounded-xl p-2 flex items-center justify-center hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 cursor-pointer"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </div>
      <p className="mt-1.5 text-[10px] text-muted-foreground/60 text-right">
        Ctrl+Enter · {pendingFiles.length}/{MAX_ATTACHMENTS} {t("support.attachments").toLowerCase()}
      </p>
    </div>
  );
}

export function OrganizerSupportPage() {
  const { t } = useTranslation();
  const navigate = useAppNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("ticket");

  const [tickets, setTickets] = useState<TicketRow[]>([]);
  const [weddings, setWeddings] = useState<WeddingSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [activeTab, setActiveTab] = useState<"active" | "resolved">("active");
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    Promise.all([organizerSupportApi.list(), organizerWeddingsApi.list()])
      .then(([sr, wr]) => {
        setTickets((sr.data ?? []) as TicketRow[]);
        setHasMore((sr.data ?? []).length >= 20);
        setWeddings(wr.data?.weddings ?? []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const fetchMore = async () => {
    if (!tickets.length || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await organizerSupportApi.list(tickets[tickets.length - 1].created_at);
      const rows = (res.data ?? []) as TicketRow[];
      setTickets((prev) => [...prev, ...rows]);
      setHasMore(rows.length >= 20);
    } finally { setLoadingMore(false); }
  };

  const filteredTickets = tickets.filter((tk) =>
    activeTab === "active"
      ? tk.status === "open" || tk.status === "in_review"
      : tk.status === "resolved" || tk.status === "closed"
  );

  const selectTicket = (id: string) => {
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      navigate(paths.supportDetail(id));
    } else {
      setSearchParams({ ticket: id });
    }
  };

  const handleCreated = (id: string) => {
    setShowModal(false);
    if (window.innerWidth < 768) {
      navigate(paths.supportDetail(id));
    } else {
      setSearchParams({ ticket: id });
    }
  };

  return (
    <div className="flex h-screen flex-col bg-background">
      <OrganizerPageHeader />

      {/* Split-pane body — centred within max-w-7xl to match header */}
      <div className="flex flex-1 overflow-hidden justify-center">
        <div className="flex w-full max-w-7xl overflow-hidden">
        {/* Left: Ticket List */}
        <TicketListPanel
          tickets={filteredTickets}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={hasMore}
          activeTab={activeTab}
          selectedId={selectedId}
          onTabChange={setActiveTab}
          onSelect={selectTicket}
          onLoadMore={fetchMore}
          onNew={() => setShowModal(true)}
        />

        {/* Right: Chat Detail (desktop only) */}
        <section className="hidden md:flex flex-1 overflow-hidden border-l border-primary/10">
          {selectedId ? (
            <ChatDetailPanel
              key={selectedId}
              ticketId={selectedId}
              onBack={() => setSearchParams({})}
            />
          ) : (
            <EmptyDetailState onNew={() => setShowModal(true)} />
          )}
        </section>
        </div>
      </div>

      {showModal && (
        <NewTicketModal
          weddings={weddings}
          onCreated={handleCreated}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

/**
 * Ticket detail — mobile-only full-screen thread view.
 * On desktop (md+), redirects to /support?ticket=:id so the split-pane
 * layout is used instead of this standalone page.
 */
export function OrganizerSupportDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useAppNavigate();

  useEffect(() => {
    if (id && window.innerWidth >= 768) {
      navigate(paths.supportWithTicket(id), { replace: true });
    }
  }, [id, navigate]);

  if (!id) return null;

  return (
    <div className="flex h-screen flex-col bg-background">
      <ChatDetailPanel
        ticketId={id}
        onBack={() => navigate(paths.support)}
      />
    </div>
  );
}

function CsatRating({ ticketId, onRated }: { ticketId: string; onRated: (score: number) => void }) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [rateError, setRateError] = useState("");

  const handleRate = async (score: number) => {
    setSubmitting(true);
    setRateError("");
    try {
      await organizerSupportApi.rateCsat(ticketId, score);
      onRated(score);
    } catch (err) {
      setRateError(err instanceof Error ? err.message : t("common.unknownError"));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 space-y-2">
      <p className="text-sm font-semibold">{t("support.csatTitle")}</p>
      <p className="text-xs text-muted-foreground">{t("support.csatHint")}</p>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            aria-label={`${star} / 5`}
            disabled={submitting}
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(0)}
            onClick={() => handleRate(star)}
            className={cn(
              "text-2xl transition-transform hover:scale-110 disabled:opacity-50 cursor-pointer",
              hovered >= star ? "text-yellow-400" : "text-muted-foreground/30"
            )}
          >
            ★
          </button>
        ))}
      </div>
      {rateError && <p className="text-xs text-destructive">{rateError}</p>}
    </div>
  );
}

function MessageBubble({
  authorType, content, createdAt, label, attachmentKeys, ticketId,
}: {
  authorType: "organizer" | "admin"; content: string; createdAt: string;
  label: string; attachmentKeys: string | null; ticketId: string;
}) {
  const isOrganizer = authorType === "organizer";
  let parsedKeys: string[] = [];
  try { parsedKeys = attachmentKeys ? JSON.parse(attachmentKeys) : []; } catch { parsedKeys = []; }
  const time = new Date(createdAt + (createdAt.includes("Z") ? "" : "Z"))
    .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  return (
    <div className={cn("flex gap-3", isOrganizer ? "flex-row-reverse" : "flex-row")}>
      {!isOrganizer && (
        <div className="h-8 w-8 rounded-xl bg-primary/15 flex items-center justify-center shrink-0 mt-1">
          <Headphones className="h-4 w-4 text-primary" />
        </div>
      )}
      <div className={cn("flex flex-col gap-1 max-w-[80%]", isOrganizer ? "items-end" : "items-start")}>
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground px-1">
          {label}
        </span>
        <div className={cn(
          "px-4 py-3 text-sm leading-relaxed shadow-sm",
          isOrganizer
            ? "bg-primary text-white rounded-2xl rounded-tr-sm shadow-primary/20"
            : "bg-muted text-foreground rounded-2xl rounded-tl-sm"
        )}>
          {content && <p className="whitespace-pre-wrap">{content}</p>}
          {parsedKeys.length > 0 && (
            <div className={cn("flex flex-wrap gap-2", content ? "mt-2" : "")}>
              {parsedKeys.map((key) => (
                <a key={key} href={organizerSupportApi.attachmentUrl(ticketId, key)} target="_blank" rel="noopener noreferrer">
                  <img
                    src={organizerSupportApi.attachmentUrl(ticketId, key)}
                    alt="attachment"
                    className="max-h-48 max-w-xs rounded-sm border object-cover cursor-pointer hover:opacity-90 transition-opacity"
                  />
                </a>
              ))}
            </div>
          )}
        </div>
        <span className="text-[10px] text-muted-foreground px-1">{time}</span>
      </div>
    </div>
  );
}
